/* ==========================================================================
   Content-driven nav collapse
   The desktop menu switches to the hamburger when it actually stops fitting,
   not at a guessed viewport width. Editors keep adding longer menu labels, so
   any hard-coded breakpoint is wrong the day after they do.

   Requires:
   - #sticky-header (.header__sticky) as the measured row
   - .header__nav > .header__menu (desktop menu)
   - .header__hamburger (portal + langswitcher + hamburger cluster)
   Toggles `nav-compact` on <html>; CSS does the rest.
   -------------------------------------------------------------------------- */

(() => {
	const root = document.documentElement;
	const sticky = document.getElementById('sticky-header');
	if (!sticky) return;

	const nav = sticky.querySelector('.header__nav');
	const menu = nav && nav.querySelector('.header__menu');
	if (!nav || !menu) return;

	const logo = sticky.querySelector('.header__logo');
	const actions = sticky.querySelector('.header__hamburger');

	const COMPACT = 'nav-compact';
	const MEASURING = 'nav-measuring';
	const SAFETY = 24; // px of breathing room so labels never touch the edge

	let threshold = Infinity;

	const px = (value) => {
		const n = parseFloat(value);
		return Number.isFinite(n) ? n : 0;
	};

	/* Width the header needs to render the full menu on a single line.
	   Measured in the expanded state, off-screen, so it also works while the
	   menu is already collapsed. */
	const measure = () => {
		const wasCompact = root.classList.contains(COMPACT);
		root.classList.remove(COMPACT);
		root.classList.add(MEASURING);

		const styles = getComputedStyle(sticky);
		const gap = px(styles.columnGap);
		const padding = px(styles.paddingLeft) + px(styles.paddingRight);

		threshold = menu.scrollWidth
			+ (logo ? logo.getBoundingClientRect().width : 0)
			+ (actions ? actions.scrollWidth : 0)
			+ (gap * 2) + padding + SAFETY;

		root.classList.remove(MEASURING);
		if (wasCompact) root.classList.add(COMPACT);
	};

	const apply = () => {
		/* Opening the drawer adds `no-scroll` to <body>, which removes the
		   scrollbar and widens the row by ~15px. Right at the threshold that
		   would un-collapse the nav underneath the open drawer, so hold state
		   while it is open. */
		if (document.body.classList.contains('no-scroll')) return;

		root.classList.toggle(COMPACT, sticky.clientWidth < threshold);
	};

	const update = () => {
		measure();
		apply();
	};

	root.classList.add('nav-js');
	update();

	/* Labels shift once the webfont lands, so the first measure is provisional. */
	if (document.fonts && document.fonts.ready) {
		document.fonts.ready.then(update);
	}

	let raf = 0;
	const schedule = () => {
		cancelAnimationFrame(raf);
		raf = requestAnimationFrame(apply);
	};

	if ('ResizeObserver' in window) {
		new ResizeObserver(schedule).observe(sticky);
	} else {
		window.addEventListener('resize', schedule);
	}

	/* Zoom, orientation change and langswitcher swaps all change label widths. */
	window.addEventListener('orientationchange', update);
})();
